import * as React from 'react'
import {
  FaIcon,
  faIdBadge,
  faArrowLeft,
  faArrowRight,
} from '@/lib/fa'
import { TaskStatusWrapper } from '@/modules/shared/components'
import ViewMode from './ViewMode'

import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { RouteComponentProps, withRouter } from '@/app/navigation/withRouter'
import { useSideSelectionStore } from '../../stores/sideSelectionStore'

interface DriverViewControlBarLayoutProps extends RouteComponentProps {
  blockedView?: boolean
  fullName: string
  children?: React.ReactNode
  companyId: string
}

const DriverViewControlBarLayout: React.FC<DriverViewControlBarLayoutProps> = ({
  fullName,
  children,
  history,
  match,
}) => {
  const {
    setSelectedDriverId,
    individualDrivers,
    selectedDriverId,
    basicDrivers,
    selectedIndividualGroupId,
    actualGroup,
    actualGroupDriverIds,
    search,
  } = useDrivers()

  const { sideCheckedIds } = useSideSelectionStore()

  const pendingGroupSwitchRef = React.useRef(false)
  const isFirstGroupRunRef = React.useRef(true)

  React.useEffect(() => {
    if (isFirstGroupRunRef.current) {
      isFirstGroupRunRef.current = false
      return
    }
    pendingGroupSwitchRef.current = true
  }, [selectedIndividualGroupId])

  React.useEffect(() => {
    if (pendingGroupSwitchRef.current) {
      if (individualDrivers.length === 0) return
      pendingGroupSwitchRef.current = false
      navigateToDriver(individualDrivers[0].id)
      return
    }

    if (individualDrivers.length === 0) return
    const urlId = match.params.driverId ? Number(match.params.driverId) : null
    if (urlId) {
      const isKnownDriver =
        individualDrivers.some((d) => d.id === urlId) ||
        basicDrivers.some((d) => d.id === urlId)
      if (isKnownDriver) {
        if (selectedDriverId !== urlId) setSelectedDriverId(urlId)
        return
      }
      if (basicDrivers.length === 0) return
    }
    if (!individualDrivers.some((d) => d.id === selectedDriverId)) {
      navigateToDriver(individualDrivers[0]?.id)
    }
  }, [individualDrivers, basicDrivers])

  const [isDriverPickerOpen, setIsDriverPickerOpen] = React.useState(false)

  const navigateToDriver = (driverId: number) => {
    setSelectedDriverId(driverId)
    const { pathname, search: qs, hash } = history.location
    if (/\/driver\/[^/]+/.test(pathname)) {
      const newPath = pathname.replace(/(\/driver\/)[^/]+/, `$1${driverId}`)
      history.push(newPath + qs + hash)
    }
  }

  const urlDriverId = match.params.driverId
    ? Number(match.params.driverId)
    : null

  const navList = React.useMemo<{ id: number }[]>(() => {
    const q = search?.toLowerCase() ?? ''

    if (sideCheckedIds.length > 0) {
      const checkedSet = new Set(sideCheckedIds)
      return basicDrivers
        .filter((d) => checkedSet.has(d.id))
        .filter((d) => !q || (d.fullName ?? '').toLowerCase().includes(q))
    }

    let candidates = basicDrivers
    if (actualGroup?.id === -1) {
      candidates = basicDrivers.filter((d) => d.isActive)
    } else if (actualGroup?.id === -2) {
      candidates = basicDrivers.filter((d) => !d.isActive)
    } else if (actualGroup && actualGroup.id > 0) {
      const groupIds = new Set(actualGroupDriverIds)
      candidates = basicDrivers.filter((d) => groupIds.has(d.id) && d.isActive)
    }

    return candidates.filter(
      (d) => !q || (d.fullName ?? '').toLowerCase().includes(q),
    )
  }, [sideCheckedIds, basicDrivers, actualGroup, actualGroupDriverIds, search])

  const currentIndex = React.useMemo(
    () => navList.findIndex((d) => d.id === (urlDriverId ?? selectedDriverId)),
    [navList, urlDriverId, selectedDriverId],
  )

  const onPrev = React.useCallback(() => {
    if (navList.length === 0) return
    const prevIndex = currentIndex <= 0 ? navList.length - 1 : currentIndex - 1
    navigateToDriver(navList[prevIndex].id)
  }, [navList, currentIndex])

  const onNext = React.useCallback(() => {
    if (navList.length === 0) return
    const nextIndex = currentIndex >= navList.length - 1 ? 0 : currentIndex + 1
    navigateToDriver(navList[nextIndex].id)
  }, [navList, currentIndex])

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        document.activeElement &&
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(
          (document.activeElement as HTMLElement).tagName,
        )
      )
        return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        onPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        onNext()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onPrev, onNext])

  return (
    <TaskStatusWrapper>
      <div className="tw-relative tw-flex tw-w-full tw-flex-row tw-justify-between tw-gap-2 tw-border-b">
        <ViewMode padding/>

        <div className="tw-flex tw-flex-1 tw-flex-wrap-reverse tw-justify-between tw-gap-2 tw-px-4 tw-py-2 sm:tw-items-center sm:tw-justify-start sm:tw-gap-6">
          <div className="tw-flex tw-items-center tw-gap-5">
            <div className="tw-flex tw-items-center tw-gap-2">
              <div
                className="tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-full tw-p-2 tw-shadow-md"
                onClick={onPrev}
              >
                <FaIcon icon={faArrowLeft} className="tw-h-4 tw-w-4" />
              </div>

              <div
                className="tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-full tw-p-2 tw-shadow-md"
                onClick={onNext}
              >
                <FaIcon icon={faArrowRight} className="tw-h-4 tw-w-4" />
              </div>
            </div>
            <div className="tw-relative tw-flex tw-items-center tw-gap-1">
              <FaIcon icon={faIdBadge} className="tw-w-4" />
              <div
                className="tw-flex tw-min-w-[200px] tw-cursor-pointer tw-items-center tw-gap-2"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setIsDriverPickerOpen((prev) => !prev)
                }}
              >
                <p className="tw-text-xl tw-font-semibold tw-text-content-base">
                  {fullName}
                </p>
              </div>
            </div>
          </div>
        </div>
          <div className='tw-py-2 tw-px-4'>{children}</div>
      </div>
    </TaskStatusWrapper>
  )
}

export default withRouter(DriverViewControlBarLayout)
